/**
 * Zod primitive for JSON-serializable values.
 *
 * Use this at trust boundaries instead of `z.unknown()` when the value will
 * be stored in a Prisma JSON column, serialized over the network, or logged.
 * `z.unknown()` accepts anything (functions, Dates, Maps, Symbols, class
 * instances) — Prisma silently drops non-JSON values on write and loggers
 * may behave unpredictably. `JsonValueSchema` rejects them at parse time.
 *
 * Typical use:
 *
 *     // Before:
 *     config: z.record(z.string(), z.unknown()).optional()
 *
 *     // After:
 *     config: z.record(z.string(), JsonValueSchema).default({})
 */

import { z } from 'zod';

export const JsonPrimitiveSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type JsonPrimitive = z.infer<typeof JsonPrimitiveSchema>;

export const JsonValueSchema = z.json();
export type JsonValue = z.infer<typeof JsonValueSchema>;

/**
 * Response-side schema for a Prisma `Json` column. Validates exactly like
 * `JsonValueSchema`, but its INPUT type is `unknown`: Prisma's `JsonValue`
 * (whose `JsonObject` allows `undefined` members) is not assignable to
 * zod's `JSONType`, so a `JsonValueSchema` field would reject every row
 * typed straight from Prisma at compile time.
 */
export const PrismaJsonColumnSchema = z.unknown().pipe(JsonValueSchema);

export const JsonObjectSchema = z.record(z.string(), JsonValueSchema);
export type JsonObject = z.infer<typeof JsonObjectSchema>;

/**
 * Type guard for plain object JSON values. Narrows to Record<string, unknown>
 * without a type assertion — useful after reading Prisma Json columns when
 * you don't need full Zod validation of the nested values.
 */
export function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
